import React, { useEffect, useState } from 'react';
import { BubbleX } from '@@components';
import './index.less';
import { s3UploadApi } from '@@service/s3Upload';
import { withImageProxy } from '@/common/imageProxy';

export interface UserItemProps {
    content: string;
    attachments?: string[];
    className?: string;
}

const UserItem: React.FC<UserItemProps> = ({
    content,
    attachments,
    className = ''
}) => {
    const [ accessUrls, setAccessUrls ] = useState<Array<{ url: string, file_name?: string }>| undefined>(undefined);

    useEffect(() => {
        let aborted = false;
        async function resolveUrls() {
            if (!attachments || attachments.length === 0) {
                setAccessUrls(undefined);
                return;
            }
            try {
                const urlObjs: Array<{ url: string, file_name?: string }> = [];
                for (const key of attachments) {
                    try {
                        const res: any = await s3UploadApi.getAccessUrl(key);
                        if (res?.access_url) {
                            urlObjs.push({ url: res.access_url, file_name: res.file_name });
                        }
                    } catch (e) {
                        // 单个失败忽略，继续其他
                    }
                }
                if (!aborted) setAccessUrls(urlObjs);
            } catch (_e) {
                if (!aborted) setAccessUrls(undefined);
            }
        }
        resolveUrls();
        return () => {
            aborted = true;
        };
    }, [ attachments ]);

    // 仅在解析出访问URL后再展示，避免以fileKey作为src
    const displayAttachments = accessUrls;

    // 判断文件类型
    const getFileType = (url: string) => {
        const lowerUrl = url.toLowerCase();

        // 检查URL参数中是否包含文件类型信息
        const urlParams = new URLSearchParams(lowerUrl.split('?')[1] || '');
        const contentType = urlParams.get('content-type') || urlParams.get('contenttype');

        // 根据Content-Type判断
        if (contentType) {
            if (contentType.startsWith('image/')) return 'image';
            if (contentType === 'application/pdf') return 'pdf';
            if (contentType === 'text/html') return 'html';
        }

        // 根据文件扩展名判断
        if (lowerUrl.includes('.pdf')) return 'pdf';
        if (lowerUrl.includes('.html') || lowerUrl.includes('.htm')) return 'html';
        if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|heic)$/)) return 'image';

        // 根据URL路径中的关键词判断
        if (lowerUrl.includes('/images/') || lowerUrl.includes('/image/')) return 'image';
        if (lowerUrl.includes('/pdf/') || lowerUrl.includes('/documents/')) return 'pdf';
        if (lowerUrl.includes('/html/') || lowerUrl.includes('/web/')) return 'html';

        // 如果无法确定类型，尝试通过fetch获取Content-Type
        // 这里我们暂时返回unknown，让用户手动判断
        return 'unknown';
    };

    // 分离图片和其他文件
    const imageAttachments = displayAttachments?.filter(item => {
        const fileType = getFileType(item.url);
        return fileType === 'image' || fileType === 'unknown';
    }) || [];
    const otherAttachments = displayAttachments?.filter(item => {
        const fileType = getFileType(item.url);
        return fileType !== 'image' && fileType !== 'unknown';
    }) || [];

    // 获取basePath（如/intelligentResolver）

    return (
        <div className={`user-item ${className}`}>
            <div className="user-item-content">
                <BubbleX
                    content={content}
                    messageRender={() => (
                        <div className="user-message-content">
                            {/* 图片直接渲染 */}
                            {imageAttachments.length > 0 && (
                                <div className="user-message-images">
                                    {imageAttachments.map((imgItem, index) => (
                                        <img
                                            key={index}
                                            src={withImageProxy(imgItem.url)}
                                            alt={`Uploaded image ${index + 1}`}
                                            className="user-message-image"
                                            onError={(e) => {
                                                console.log('图片加载失败，显示为链接:', imgItem.url);
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                            }}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* 其他文件显示为链接 */}
                            {otherAttachments.length > 0 && (
                                <div className="user-message-files">
                                    {otherAttachments.map((fileItem, index) => {
                                        const fileType = getFileType(fileItem.url);
                                        return (
                                            <div key={index} className="user-message-file">
                                                {fileType === 'pdf' ? (
                                                    <a
                                                        href={fileItem.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <img
                                                            src={require('@/assets/pdf-file.png')}
                                                            alt="PDF file"
                                                            className="user-message-file-icon"
                                                        />
                                                        {fileItem.file_name ? fileItem.file_name : `PDF file ${index + 1}`}
                                                    </a>
                                                ) : fileType === 'html' ? (
                                                    <a
                                                        href={fileItem.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <img
                                                            src={require('@/assets/xls-file.png')}
                                                            alt="HTML file"
                                                            className="user-message-file-icon"
                                                        />
                                                        {fileItem.file_name ? fileItem.file_name : `HTML file ${index + 1}`}
                                                    </a>
                                                ) : (
                                                    <a
                                                        href={fileItem.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {fileItem.file_name ? `📎 ${fileItem.file_name}` : `📎 Attachment ${index + 1}`}
                                                    </a>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="user-message-text">
                                {content.split('\n').map((line, index) => (
                                    <div key={index}>
                                        {line}
                                        {index < content.split('\n').length - 1 && <br />}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    placement="end"
                    className="user-bubble"
                />
            </div>
        </div>
    );
};

export default UserItem;
